import React, {useMemo, useCallback, memo} from 'react';
import {Link} from 'react-router-dom';
import {Props} from './ac-navigation-list.proptypes';
import clsx from 'clsx';

// Imports => Constants
import {TYPES} from '@asdf-constants';

// Imports => Utils
import {AcUUID, AcIsSet, AcIsFunction, AcIsObject} from '@asdf-utils';

// Imports => Atoms
import {AcIcon} from '@asdf-atoms/ac-icon';

const _CLASSES = {
  MAIN: 'ac-navigation-list',
  WRP: 'ac-navigation-list-wrp',
  ITEM: 'ac-navigation-list__item',
  THEME: {
    DEFAULT: 'ac-navigation-list__item--default',
    DANGER: 'ac-navigation-list__item--danger',
  },
};

const AcNavigationList = memo(({items = [], className, ...rest}) => {
  const getItemClassNames = useCallback(item => {
    return clsx(
      _CLASSES.ITEM,
      AcIsSet(item) && AcIsObject(item) && item.theme && _CLASSES.THEME[item.theme.toUpperCase()],
      AcIsSet(item) && AcIsObject(item) && item.className
    );
  }, []);

  const getMainClassNames = useMemo(() => {
    return clsx(_CLASSES.MAIN);
  }, []);

  const getWrpClassNames = useMemo(() => {
    return clsx(_CLASSES.WRP, className);
  }, [className]);

  const handleCallback = (event, item) => {
    if (AcIsSet(item.callback) && AcIsFunction(item.callback)) {
      item.callback(event);
    }
  };

  const renderListItems = useMemo(() => {
    if (!items) return null;

    const collection = items;
    const len = collection.length;
    let n = 0;
    let result = [];

    for (n; n < len; n++) {
      const item = collection[n];
      let object = null;
      let icon = null;
      let suffix = null;

      if (
        AcIsObject(item) &&
        AcIsSet(item.type) &&
        (item.type === TYPES.LINK || item.type === TYPES.BUTTON)
      ) {
        const attributes = {
          callback: null,
        };
        let Tag = Link;
        let attr = {};

        if (AcIsSet(item.attributes)) {
          attr = item.attributes;
        }

        if (item.type === TYPES.BUTTON) {
          attributes.type = 'button';
          Tag = 'button';
        } else if (AcIsSet(item.href) || !AcIsSet(item.to)) {
          attributes.href = item.href || '#';
          Tag = 'a';
        } else if (AcIsSet(item.to)) {
          attributes.to = item.to;
        }

        if (AcIsSet(item.icon)) icon = <AcIcon icon={item.icon} title={item.label} />;
        if (AcIsSet(item.suffix)) suffix = <AcIcon icon={item.suffix} title={item.label} />;

        object = (
          <li
            key={AcUUID()}
            className={getItemClassNames(item)}
            role={'presentation'}
            itemProp={'name'}
          >
            <Tag
              {...attr}
              {...attributes}
              itemProp={'url'}
              role={'menuitem'}
              title={item.label}
              onClick={event => handleCallback(event, item)}
            >
              {icon}
              <span
                dangerouslySetInnerHTML={{
                  __html: item.label,
                }}
              />
              {suffix}
            </Tag>
          </li>
        );
      } else {
        object = (
          <li key={AcUUID()} className={getItemClassNames}>
            {item}
          </li>
        );
      }

      if (object) result.push(object);
    }

    return result;
  }, [items]);

  return (
    <nav className={getWrpClassNames} {...rest}>
      <ul className={getMainClassNames}>{renderListItems}</ul>
    </nav>
  );
});

AcNavigationList.displayName = 'AcNavigationList';
AcNavigationList.propTypes = Props;

export {AcNavigationList};
export default AcNavigationList;
